import { clientLogger } from './logger';
import { auth } from '../services/firebaseService';

/**
 * Enhanced fetch function that adds authentication headers and userId to requests
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
export const authenticatedFetch = async (url, options = {}) => {
  try {
    // Get user data from localStorage
    const userDataString = localStorage.getItem('userData');
    const userData = userDataString ? JSON.parse(userDataString) : null;
    
    clientLogger.debug('authenticatedFetch', {
      url,
      hasUserData: !!userData,
      userId: userData?.id,
      method: options?.method || 'GET'
    });
    
    // Try to get Firebase ID token for authentication
    let firebaseToken = null;
    if (auth.currentUser) {
      try {
        firebaseToken = await auth.currentUser.getIdToken();

        clientLogger.debug('Got Firebase ID token for request');
      } catch (error) {
        console.error('❌ Failed to get Firebase token:', error);
        clientLogger.warn('Failed to get Firebase ID token:', error);
      }
    } else {
      console.log('❌ No Firebase current user for:', url);
    }
    
    if (firebaseToken) {
      // Use Firebase ID token for authentication
      options = {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${firebaseToken}`,
          'X-User-ID': userData?.id || auth.currentUser?.uid
        },
        // Always include credentials to ensure cookies are sent with the request
        credentials: 'include'
      };
    } else if (userData) {
      // Fallback to session-based authentication
      options = {
        ...options,
        headers: {
          ...options.headers,
          'X-User-ID': userData.id
        },
        // Always include credentials to ensure cookies are sent with the request
        credentials: 'include'
      };
      
      // Add userId as query param for endpoints that require it
      const shouldAddUserId = url.includes('/api/organizations/') || 
                              url.includes('/api/users/') ||
                              url.includes('/api/brands/') ||
                              url.includes('/api/admin/');
                              
      if (shouldAddUserId) {
        const urlObj = new URL(url, window.location.origin);
        if (!urlObj.searchParams.has('userId')) {
          urlObj.searchParams.append('userId', userData.id);
          url = urlObj.toString();
          clientLogger.debug('Enhanced URL with userId:', url);
        }
      }
    } else {
      // Even if no user data, still include credentials for cookie-based auth
      options = {
        ...options,
        credentials: 'include'
      };
    }
    
    // Log any cookies being sent (for debugging)
    clientLogger.debug('Request cookies status', {
      hasCookies: document.cookie.length > 0,
      cookieLength: document.cookie.length
    });
    
    // Make the fetch request with enhanced options
    const response = await fetch(url, options);
    
    // Handle authentication errors by trying to resync session
    if (response.status === 401) {
      clientLogger.warn('Authentication error during fetch, attempting to restore session', {
        url,
        status: response.status
      });
      
      // Try to sync session and retry the fetch
      const sessionSynced = await syncUserSession();
      if (sessionSynced) {
        clientLogger.info('Session restored, retrying fetch');
        return fetch(url, options);
      } else {
        clientLogger.error('Session sync failed, unable to retry fetch');
      }
    }
    
    return response;
  } catch (error) {
    clientLogger.error('authenticatedFetch error:', error);
    throw error;
  }
};

/**
 * Synchronize the user session with the server
 * @returns {Promise<boolean>} - Whether sync was successful
 */
export const syncUserSession = async () => {
  try {
    // Get user data from localStorage
    const userDataString = localStorage.getItem('userData');
    const userData = userDataString ? JSON.parse(userDataString) : null;
    
    if (!userData) {
      clientLogger.warn('No user data in localStorage for syncUserSession');
      return false;
    }
    
    // Log the structure of userData to debug
    clientLogger.debug('Syncing user session with server', {
      userId: userData.id,
      email: userData.email,
      isAdmin: userData.is_admin,
      hasNestedUser: !!userData.user,
      cookieStatus: document.cookie.length > 0 ? 'present' : 'missing',
      dataStructure: JSON.stringify(userData).substring(0, 100) + '...'
    });
    
    // If userData contains a success and user property, it might be
    // a nested response from the login endpoint
    let userDataToSend = userData;
    if (userData.success && userData.user && typeof userData.user === 'object') {
      userDataToSend = userData.user;
      clientLogger.debug('Detected nested user data structure, using inner user object');
    }
    
    // Ensure the userData has the required fields
    if (!userDataToSend.id) {
      clientLogger.error('User data missing required ID field', { userData: userDataToSend });
      return false;
    }
    
    // Create fetch options with credentials and content type
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-ID': userDataToSend.id,
        // Add a cache-busting header to prevent cached responses
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      body: JSON.stringify({ userData: userDataToSend }),
      credentials: 'include',
      // Add cache: 'no-store' to prevent caching
      cache: 'no-store'
    };
    
    // Log the actual request body for debugging
    clientLogger.debug('Session sync request body', {
      body: JSON.stringify({ userData: userDataToSend }).substring(0, 100) + '...'
    });
    
    // Call sync-session endpoint
    const response = await fetch('/api/auth/sync-session', options);
    
    if (!response.ok) {
      const errorText = await response.text();
      clientLogger.error('Failed to sync session:', errorText, {
        status: response.status,
        statusText: response.statusText
      });
      
      // If we got a 400 bad request, the session might be corrupted
      // Force a re-login in this case
      if (response.status === 400) {
        clientLogger.warn('Session sync returned bad request, may need to re-login');
        // Clear localStorage to force re-login on next refresh
        if (confirm('Your session appears to be invalid. Would you like to log in again?')) {
          localStorage.removeItem('userData');
          window.location.href = '/auth?redirect=' + encodeURIComponent(window.location.pathname);
          return false;
        }
      }
      
      return false;
    }
    
    // Update user data if needed
    const sessionData = await response.json();
    if (sessionData.user) {
      // Store the clean user object without nesting
      localStorage.setItem('userData', JSON.stringify(sessionData.user));
      clientLogger.debug('Updated user data from session sync', {
        userId: sessionData.user.id,
        isAdmin: sessionData.user.is_admin
      });
    }
    
    // Check for cookie
    if (document.cookie.length === 0) {
      clientLogger.warn('No cookies set after successful session sync');
    } else {
      clientLogger.debug('Cookies present after session sync');
    }
    
    return true;
  } catch (error) {
    clientLogger.error('syncUserSession error:', error);
    return false;
  }
};

/**
 * Helper function specifically for organization API calls
 */
export const fetchOrganizations = async () => {
  // For browser development or if MOCK_DATA env is set, return mock data
  if (typeof window !== 'undefined' && (process.env.REACT_APP_MOCK_DATA === 'true' || process.env.VITE_MOCK_DATA === 'true')) {
    console.log('Using mock organization data');
    return [
      {
        id: 'mock-org-1',
        name: 'Demo Organization',
        createdAt: new Date().toISOString(),
        role: 'admin'
      }
    ];
  }
  
  try {
    // Ensure session is synced before fetching organizations
    const sessionSynced = await syncUserSession();
    
    if (!sessionSynced) {
      clientLogger.warn('fetchOrganizations: Session sync failed, proceeding with request anyway');
    }
    
    // Get user data for userId
    const userDataString = localStorage.getItem('userData');
    const userData = userDataString ? JSON.parse(userDataString) : null;
    
    if (!userData || !userData.id) {
      throw new Error('User data not available');
    }
    
    // Use the real organizations endpoint
    const url = `/api/organizations/my?userId=${userData.id}`;
    
    // Create request options with credentials for session-based auth
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    const options = {
      method: 'GET',
      headers: {
        'X-User-ID': userData.id,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      },
      credentials: 'include',
      signal: controller.signal
    };
    
    // Make the fetch request
    clientLogger.debug('Fetching organizations via debug endpoint', { 
      url, 
      userId: userData.id 
    });
    
    try {
      const response = await fetch(url, options);
      
      // Clear the timeout
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        clientLogger.error('Error fetching organizations:', errorText, {
          status: response.status,
          statusText: response.statusText
        });
        throw new Error(`Failed to fetch organizations: ${response.status} ${errorText}`);
      }
      
      return await response.json();
    } catch (fetchError) {
      // Check if this was an abort error (timeout)
      if (fetchError.name === 'AbortError') {
        throw new Error('Organization fetch request timed out');
      }
      throw fetchError;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    clientLogger.error('fetchOrganizations error:', error);
    throw error;
  }
}; 